import { useAuthOptional } from "../context/AuthProvider";
import { accountLoginUrl, type CustomerAccountModel } from "../lib/api";

/**
 * Shape of the auth-context `shop` extended with the (cross-lane) account model.
 * Lane A3 adds `accountModel` to the `/api/customer/context` response and the
 * `AuthState` `shop` type; until then it's absent, so we read it defensively and
 * default to "classic" (today's behavior). Kept local so this hook compiles
 * standalone regardless of when A3 lands.
 */
type ShopWithAccountModel = { domain: string; accountModel?: CustomerAccountModel } | null;

/**
 * Read the active storefront account model from the auth context. Defensive:
 * returns "classic" whenever the field is absent (pre-A3) or the state has no
 * shop (loading / error / preview-gate).
 */
export function useAccountModel(): CustomerAccountModel {
  const ctx = useAuthOptional();
  const state = ctx?.state;
  const shop: ShopWithAccountModel =
    state && (state.kind === "customer" || state.kind === "anon") ? state.shop : null;
  return shop?.accountModel ?? "classic";
}

/**
 * Ready-to-use storefront login URL honoring the deployment's account model.
 * Delegates to the pure `accountLoginUrl`, which derives the return path from
 * the current location when `returnPath` is omitted.
 *
 * @param returnPath optional same-origin relative path (starting "/") to return
 *   to after login; omit to use the current `pathname + search`.
 */
export function useAccountLoginUrl(returnPath?: string): string {
  const accountModel = useAccountModel();
  return accountLoginUrl(accountModel, returnPath);
}
